import { useMemo } from 'react';
import { useActiveContext } from '@/shared/hooks/useActiveContext';
import { useLocation } from './useLocations';
import type { CommercialModule } from '../types';

const CORE_ONLY: CommercialModule[] = ['core_stay'];

/**
 * Returns the active commercial modules for the currently selected location.
 *
 * Behavior:
 *  - If no active location context (e.g. company-level pages) → returns `null`
 *    so callers can decide whether to gate or not (usually: don't gate).
 *  - While the location is loading → returns `null`.
 *  - If the location has no `activeModules` → falls back to `['core_stay']`.
 */
export function useActiveLocationModules(): CommercialModule[] | null {
  const { activeLocationId } = useActiveContext();
  const { data: location, isLoading } = useLocation(activeLocationId ?? undefined);

  return useMemo<CommercialModule[] | null>(() => {
    if (!activeLocationId) return null;
    if (isLoading) return null;
    if (!location) return CORE_ONLY;
    return location.activeModules && location.activeModules.length > 0
      ? location.activeModules
      : CORE_ONLY;
  }, [activeLocationId, isLoading, location]);
}
